import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Button,
  Paper,
  Tooltip,
  Stack,
} from '@mui/material';

import AccessibleIcon from '@mui/icons-material/Accessible';
import CloseIcon from '@mui/icons-material/Close';
import AnimationIcon from '@mui/icons-material/Animation';
import ContrastIcon from '@mui/icons-material/Contrast';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import LinkIcon from '@mui/icons-material/Link';
import TitleIcon from '@mui/icons-material/Title';
import ImageIcon from '@mui/icons-material/Image';
import MouseIcon from '@mui/icons-material/Mouse';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAccessibility } from '../Context/AccessibilityContext';
import { useNavigation } from '../Context/NavigationContext';
import { useTheme } from '@mui/material/styles';

const AccessibilityToolbar = ({ showFloatingButton = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [isVisible, setIsVisible] = useState(true); // Control button visibility
  const { isMobileNavOpen } = useNavigation();
  const { accessibilitySettings, updateSetting, resetSettings, readText, readSelectedText, stopReading } = useAccessibility();
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';

  const toggleDrawer = (open) => {
    setIsOpen(open);
  };

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  // Add Ctrl+F2 keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'F2') {
        event.preventDefault();
        setIsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleReadPageContent = () => {
    if (isReading) {
      stopReading();
      setIsReading(false);
    } else {
      // Get main content text
      const mainContent = document.querySelector('main') || document.body;
      const textContent = mainContent.innerText;
      readText(textContent);
      setIsReading(true);
    }
  };

  const handleReadSelection = () => {
    const hasSelection = readSelectedText();
    if (!hasSelection) {
      alert('الرجاء تحديد النص الذي تريد قراءته');
    }
  };

  const handleTextSizeChange = (adjustment) => {
    const newSize = accessibilitySettings.textSize + adjustment;
    if (newSize >= 80 && newSize <= 150) {
      updateSetting('textSize', newSize);
    }
  };

  const handleZoomChange = (adjustment) => {
    const newZoom = accessibilitySettings.zoomLevel + adjustment;
    if (newZoom >= 100 && newZoom <= 200) {
      updateSetting('zoomLevel', newZoom);
    }
  };

  return (
    <>
      {/* Floating Accessibility Button - Bottom Corner */}
      {showFloatingButton && isVisible && !isMobileNavOpen && (
        <Tooltip title="إعدادات إمكانية الوصول (Ctrl+F2)" placement="top">
          <IconButton
            onClick={() => toggleDrawer(!isOpen)}
            sx={{
              position: 'fixed',
              bottom: 20,
              left: '20px !important',
              width: { xs: 44, md: 60 },
              height: { xs: 44, md: 60 },
              backgroundColor: { xs: 'white', md: 'primary.main' },
              color: { xs: 'grey.700', md: 'white' },
              border: { xs: '1px solid rgba(0,0,0,0.12)', md: 'none' },
              zIndex: 1300,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              '&:hover': {
                backgroundColor: { xs: 'rgba(0,0,0,0.04)', md: 'primary.dark' },
                transform: { xs: 'scale(1.05)', md: 'scale(1.1)' },
              },
              transition: 'all 0.3s ease',
            }}
            aria-label="فتح إعدادات إمكانية الوصول (اضغط Ctrl+F2)"
          >
            <AccessibleIcon sx={{ fontSize: { xs: 22, md: 32 } }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Mobile Drawer - Bottom drawer next to button */}
      <Drawer
      anchor={'left'}
        open={isOpen}
        onClose={() => toggleDrawer(false)}
        sx={{
          display: { xs: 'block', md: 'none' }, // Mobile only
          '& .MuiDrawer-paper': {
            width: 'calc(100vw - 110px)', // Full width minus button area
            maxWidth: 350,
            height: '60vh', // 60% of viewport height
            bottom: 0,
            left: 110,  // Start beside the button on the right
            top: 'auto',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,

          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessibleIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                إعدادات إمكانية الوصول
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Ctrl+F2
              </Typography>
              <IconButton onClick={() => toggleDrawer(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <Stack spacing={3}>
              {/* Block Animations */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AnimationIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    الرسوم المتحركة
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant={accessibilitySettings.blockAnimations ? 'contained' : 'outlined'}
                  onClick={() => updateSetting('blockAnimations', !accessibilitySettings.blockAnimations)}
                  startIcon={<AnimationIcon />}
                >
                  {accessibilitySettings.blockAnimations ? 'إلغاء حظر الحركة' : 'حظر الحركة'}
                </Button>
              </Paper>

              {/* Color Contrast */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ContrastIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    تباين الألوان
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.colorContrast === 'uncolored' ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('colorContrast', accessibilitySettings.colorContrast === 'uncolored' ? 'normal' : 'uncolored')}
                  >
                    عرض بدون ألوان
                  </Button>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.colorContrast === 'bright' ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('colorContrast', accessibilitySettings.colorContrast === 'bright' ? 'normal' : 'bright')}
                  >
                    تباين ساطع
                  </Button>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.colorContrast === 'inverted' ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('colorContrast', accessibilitySettings.colorContrast === 'inverted' ? 'normal' : 'inverted')}
                  >
                    عكس الألوان
                  </Button>
                </Stack>
              </Paper>

              {/* Text Size */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <FormatSizeIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    حجم النص
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                  الحجم الحالي: {accessibilitySettings.textSize}%
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => handleTextSizeChange(10)}
                    disabled={accessibilitySettings.textSize >= 150}
                    sx={{ flex: 1 }}
                  >
                    تكبير النص
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleTextSizeChange(-10)}
                    disabled={accessibilitySettings.textSize <= 80}
                    sx={{ flex: 1 }}
                  >
                    تصغير النص
                  </Button>
                </Stack>
                <Box sx={{ mt: 2 }}>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.readableFont ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('readableFont', !accessibilitySettings.readableFont)}
                  >
                    خط قابل للقراءة
                  </Button>
                </Box>
              </Paper>

              {/* Highlighting Content */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LinkIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    تمييز المحتوى
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.underlineLinks ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('underlineLinks', !accessibilitySettings.underlineLinks)}
                    startIcon={<LinkIcon />}
                  >
                    تسطير الروابط
                  </Button>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.underlineHeaders ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('underlineHeaders', !accessibilitySettings.underlineHeaders)}
                    startIcon={<TitleIcon />}
                  >
                    تسطير العناوين
                  </Button>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.imageTitles ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('imageTitles', !accessibilitySettings.imageTitles)}
                    startIcon={<ImageIcon />}
                  >
                    عناوين الصور
                  </Button>
                </Stack>
              </Paper>

              {/* Cursor and Zoom */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MouseIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    المؤشر والتكبير
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.cursorSize === 'white' ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('cursorSize', accessibilitySettings.cursorSize === 'white' ? 'normal' : 'white')}
                  >
                    مؤشر أبيض كبير
                  </Button>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.cursorSize === 'black' ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('cursorSize', accessibilitySettings.cursorSize === 'black' ? 'normal' : 'black')}
                  >
                    مؤشر أسود كبير
                  </Button>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                  التكبير: {accessibilitySettings.zoomLevel}%
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => handleZoomChange(10)}
                    disabled={accessibilitySettings.zoomLevel >= 200}
                    startIcon={<ZoomInIcon />}
                    sx={{ flex: 1 }}
                  >
                    تكبير الشاشة
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleZoomChange(-10)}
                    disabled={accessibilitySettings.zoomLevel <= 100}
                    sx={{ flex: 1 }}
                  >
                    تصغير
                  </Button>
                </Stack>
              </Paper>

              {/* Screen Reader */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <RecordVoiceOverIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    قارئ المحتوى
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant={isReading ? 'contained' : 'outlined'}
                    color={isReading ? 'error' : 'primary'}
                    onClick={handleReadPageContent}
                    startIcon={isReading ? <StopCircleIcon /> : <RecordVoiceOverIcon />}
                  >
                    {isReading ? 'إيقاف القراءة' : 'قراءة المحتوى'}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleReadSelection}
                    startIcon={<RecordVoiceOverIcon />}
                  >
                    قراءة النص المحدد
                  </Button>
                </Stack>
              </Paper>

              {/* Reset Settings */}
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={resetSettings}
                startIcon={<RestartAltIcon />}
                sx={{ py: 1.5 }}
              >
                إعادة تعيين الإعدادات
              </Button>

              {/* Hide Accessibility Button */}
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                onClick={() => {
                  setIsVisible(false);
                  toggleDrawer(false);
                }}
                startIcon={<VisibilityOffIcon />}
                sx={{ py: 1.5 }}
              >
                إخفاء زر إمكانية الوصول
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', mt: 1 }}>
                للإظهار مرة أخرى: اضغط Ctrl+F2
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      {/* Desktop Drawer - Right side next to button, positioned from bottom */}
      <Drawer
      anchor={'left'}
        open={isOpen}
        onClose={() => toggleDrawer(false)}
        sx={{
          display: { xs: 'none', md: 'block' }, // Desktop only
          '& .MuiDrawer-paper': {
            position: 'absolute',
            top: 'auto',
            bottom: 20, // Align near bottom
            left: 120, // Next to the floating button
            width: 380,
            height: '65vh', // 60-70% height
            borderRadius: 3,
            direction: isRTL ? 'rtl' : 'ltr',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessibleIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                إعدادات إمكانية الوصول
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Ctrl+F2
              </Typography>
              <IconButton onClick={() => toggleDrawer(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <Stack spacing={3}>
              {/* Block Animations */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AnimationIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    الرسوم المتحركة
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant={accessibilitySettings.blockAnimations ? 'contained' : 'outlined'}
                  onClick={() => updateSetting('blockAnimations', !accessibilitySettings.blockAnimations)}
                  startIcon={<AnimationIcon />}
                >
                  {accessibilitySettings.blockAnimations ? 'إلغاء حظر الحركة' : 'حظر الحركة'}
                </Button>
              </Paper>

              {/* Color Contrast */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ContrastIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    تباين الألوان
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.colorContrast === 'uncolored' ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('colorContrast', accessibilitySettings.colorContrast === 'uncolored' ? 'normal' : 'uncolored')}
                  >
                    عرض بدون ألوان
                  </Button>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.colorContrast === 'bright' ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('colorContrast', accessibilitySettings.colorContrast === 'bright' ? 'normal' : 'bright')}
                  >
                    تباين ساطع
                  </Button>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.colorContrast === 'inverted' ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('colorContrast', accessibilitySettings.colorContrast === 'inverted' ? 'normal' : 'inverted')}
                  >
                    عكس الألوان
                  </Button>
                </Stack>
              </Paper>

              {/* Text Size */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <FormatSizeIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    حجم النص
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                  الحجم الحالي: {accessibilitySettings.textSize}%
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => handleTextSizeChange(10)}
                    disabled={accessibilitySettings.textSize >= 150}
                    sx={{ flex: 1 }}
                  >
                    تكبير النص
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleTextSizeChange(-10)}
                    disabled={accessibilitySettings.textSize <= 80}
                    sx={{ flex: 1 }}
                  >
                    تصغير النص
                  </Button>
                </Stack>
                <Box sx={{ mt: 2 }}>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.readableFont ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('readableFont', !accessibilitySettings.readableFont)}
                  >
                    خط قابل للقراءة
                  </Button>
                </Box>
              </Paper>

              {/* Highlighting Content */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LinkIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    تمييز المحتوى
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.underlineLinks ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('underlineLinks', !accessibilitySettings.underlineLinks)}
                    startIcon={<LinkIcon />}
                  >
                    تسطير الروابط
                  </Button>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.underlineHeaders ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('underlineHeaders', !accessibilitySettings.underlineHeaders)}
                    startIcon={<TitleIcon />}
                  >
                    تسطير العناوين
                  </Button>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.imageTitles ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('imageTitles', !accessibilitySettings.imageTitles)}
                    startIcon={<ImageIcon />}
                  >
                    عناوين الصور
                  </Button>
                </Stack>
              </Paper>

              {/* Cursor and Zoom */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MouseIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    المؤشر والتكبير
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.cursorSize === 'white' ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('cursorSize', accessibilitySettings.cursorSize === 'white' ? 'normal' : 'white')}
                  >
                    مؤشر أبيض كبير
                  </Button>
                  <Button
                    fullWidth
                    variant={accessibilitySettings.cursorSize === 'black' ? 'contained' : 'outlined'}
                    onClick={() => updateSetting('cursorSize', accessibilitySettings.cursorSize === 'black' ? 'normal' : 'black')}
                  >
                    مؤشر أسود كبير
                  </Button>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                  التكبير: {accessibilitySettings.zoomLevel}%
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => handleZoomChange(10)}
                    disabled={accessibilitySettings.zoomLevel >= 200}
                    startIcon={<ZoomInIcon />}
                    sx={{ flex: 1 }}
                  >
                    تكبير الشاشة
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleZoomChange(-10)}
                    disabled={accessibilitySettings.zoomLevel <= 100}
                    sx={{ flex: 1 }}
                  >
                    تصغير
                  </Button>
                </Stack>
              </Paper>

              {/* Screen Reader */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <RecordVoiceOverIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    قارئ المحتوى
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant={isReading ? 'contained' : 'outlined'}
                  color={isReading ? 'error' : 'primary'}
                  onClick={handleReadPageContent}
                  startIcon={isReading ? <StopCircleIcon /> : <RecordVoiceOverIcon />}
                >
                  {isReading ? 'إيقاف القراءة' : 'قراءة المحتوى'}
                </Button>
              </Paper>

              {/* Reset Settings */}
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={resetSettings}
                startIcon={<RestartAltIcon />}
                sx={{ py: 1.5 }}
              >
                إعادة تعيين الإعدادات
              </Button>

              {/* Hide Accessibility Button */}
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                onClick={() => {
                  setIsVisible(false);
                  toggleDrawer(false);
                }}
                startIcon={<VisibilityOffIcon />}
                sx={{ py: 1.5 }}
              >
                إخفاء زر إمكانية الوصول
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', mt: 1 }}>
                للإظهار مرة أخرى: اضغط Ctrl+F2
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default AccessibilityToolbar;
